import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { CheckSquare, Check, Clock, Plus, Sparkles, Flame, Loader2 } from "lucide-react";

export function TasksPanel({ tasks, taskText, setTaskText, addTask, toggleTask }) {
  return (
    <div className="bg-white border border-slate-200 p-5" data-testid="lead-tasks">
      <h3 className="text-sm font-['Outfit'] font-medium text-[#0F172A] mb-3 flex items-center gap-2"><CheckSquare className="w-4 h-4 text-[#0033CC]" />Next Steps</h3>
      <div className="space-y-2 mb-3">
        {(tasks || []).map(t => (
          <div key={t.id} className="flex items-center gap-3 text-sm">
            <button onClick={() => toggleTask(t.id, t.completed)} className={`w-5 h-5 border flex items-center justify-center ${t.completed ? "bg-[#0033CC] border-[#0033CC]" : "border-slate-300"}`} data-testid={`task-toggle-${t.id}`}>
              {t.completed && <Check className="w-3 h-3 text-white" />}
            </button>
            <span className={`font-['IBM_Plex_Sans'] ${t.completed ? "line-through text-slate-400" : "text-slate-700"}`}>{t.title}</span>
          </div>
        ))}
      </div>
      <div className="flex gap-2">
        <Input value={taskText} onChange={e => setTaskText(e.target.value)} onKeyDown={e => e.key === "Enter" && addTask()} placeholder="Add next step..." className="flex-1 rounded-none border-slate-300 h-8 text-sm" data-testid="task-input" />
        <Button onClick={addTask} size="sm" className="rounded-none bg-[#0033CC] text-white h-8 text-xs font-['Outfit']" data-testid="task-add-btn">Add</Button>
      </div>
    </div>
  );
}

export function ActivityTimeline({ activity }) {
  const formatEST = (isoStr) => {
    if (!isoStr) return "";
    try {
      // Handle various ISO formats: with Z, with +00:00, or bare
      let str = isoStr;
      if (!str.endsWith("Z") && !str.includes("+") && !str.includes("-", 10)) {
        str = str + "Z";
      }
      const d = new Date(str);
      if (isNaN(d.getTime())) return isoStr.split("T")[0] || "";
      return d.toLocaleString("en-US", { timeZone: "America/New_York", month: "short", day: "numeric", hour: "numeric", minute: "2-digit", hour12: true }) + " EST";
    } catch { return isoStr?.split("T")[0] || ""; }
  };
  return (
    <div className="bg-white border border-slate-200 p-5" data-testid="lead-activity">
      <h3 className="text-sm font-['Outfit'] font-medium text-[#0F172A] mb-3 flex items-center gap-2"><Clock className="w-4 h-4 text-[#0033CC]" />Activity Timeline</h3>
      <div className="space-y-3">
        {(activity || []).slice(0, 20).map(a => (
          <div key={a.id} className="flex gap-3 text-xs">
            <div className="w-1.5 h-1.5 rounded-full bg-[#0033CC] mt-1.5 flex-shrink-0" />
            <div className="flex-1">
              <span className="font-['IBM_Plex_Sans'] text-slate-700">{a.description}</span>
              <span className="text-slate-400 ml-2 font-['JetBrains_Mono'] text-[10px]">{formatEST(a.created_at)}</span>
            </div>
          </div>
        ))}
        {(!activity || activity.length === 0) && <p className="text-xs text-slate-400">No activity yet</p>}
      </div>
    </div>
  );
}

export function DealScoreBreakdown({ dealScore, components }) {
  if (!components) return null;
  return (
    <div className="bg-white border border-slate-200 p-5" data-testid="deal-score-breakdown">
      <h3 className="text-sm font-['Outfit'] font-medium text-[#0F172A] mb-3 flex items-center gap-2">
        <Flame className={`w-4 h-4 ${dealScore >= 60 ? "text-red-500" : "text-slate-400"}`} />Deal Score Breakdown
      </h3>
      <div className="text-center mb-4">
        <div className={`text-3xl font-['Outfit'] font-bold ${dealScore >= 60 ? "text-red-600" : dealScore >= 40 ? "text-amber-600" : "text-slate-600"}`}>{dealScore}</div>
        <p className="text-xs text-slate-400 font-['JetBrains_Mono']">/100</p>
      </div>
      <div className="space-y-2">
        {[
          { key: "audit", label: "Opportunity", weight: "30%", color: "#0033CC" },
          { key: "revenue_opportunity", label: "Revenue Gap", weight: "20%", color: "#6366F1" },
          { key: "engagement", label: "Engagement", weight: "25%", color: "#10B981" },
          { key: "recency", label: "Recency", weight: "15%", color: "#F59E0B" },
          { key: "velocity", label: "Velocity", weight: "10%", color: "#EF4444" },
        ].map(c => {
          const val = components[c.key] || 0;
          return (
            <div key={c.key}>
              <div className="flex items-center justify-between mb-0.5">
                <span className="text-xs font-['IBM_Plex_Sans'] text-slate-500">{c.label} <span className="text-slate-300">({c.weight})</span></span>
                <span className="text-xs font-['JetBrains_Mono'] text-slate-700">{val}</span>
              </div>
              <div className="h-1.5 bg-slate-100"><div className="h-full transition-all" style={{ width: `${val}%`, backgroundColor: c.color }} /></div>
            </div>
          );
        })}
      </div>
      {components.signals && (
        <div className="mt-3 pt-3 border-t border-slate-100 grid grid-cols-2 gap-2">
          <div className="text-xs"><span className="text-slate-400">Calls:</span> <span className="font-['JetBrains_Mono'] text-slate-700">{components.signals.calls}</span></div>
          <div className="text-xs"><span className="text-slate-400">Notes:</span> <span className="font-['JetBrains_Mono'] text-slate-700">{components.signals.notes}</span></div>
          <div className="text-xs"><span className="text-slate-400">Tasks done:</span> <span className="font-['JetBrains_Mono'] text-slate-700">{components.signals.tasks_completed}</span></div>
          <div className="text-xs"><span className="text-slate-400">AI Intel:</span> <span className="font-['JetBrains_Mono'] text-slate-700">{components.signals.has_ai_intel ? "Yes" : "No"}</span></div>
        </div>
      )}
    </div>
  );
}

export function DealSnapshot({ intel, generateAI, aiLoading }) {
  if (!intel) {
    return (
      <div className="bg-white border border-slate-200 p-5 text-center">
        <Sparkles className="w-8 h-8 text-slate-300 mx-auto mb-2" />
        <p className="text-sm text-slate-500 mb-3">No AI intelligence yet</p>
        <Button onClick={generateAI} disabled={aiLoading} className="bg-[#0033CC] text-white rounded-none text-xs font-['Outfit']" data-testid="generate-ai-btn">
          {aiLoading ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Sparkles className="w-3 h-3 mr-1" />} Generate Intelligence
        </Button>
      </div>
    );
  }
  return (
    <div className="bg-white border border-slate-200 p-5">
      <h3 className="text-sm font-['Outfit'] font-medium text-[#0F172A] mb-3">Deal Snapshot</h3>
      <p className="text-xs text-slate-600 font-['IBM_Plex_Sans'] mb-3">{(intel.deal_summary || "").replace(/_/g, " ")}</p>
      {intel.estimated_deal_size && (
        <div className="bg-slate-50 border border-slate-100 p-3 mb-3">
          <p className="text-xs font-['JetBrains_Mono'] text-slate-400 uppercase mb-1">Est. Deal Size</p>
          <p className="text-lg font-['Outfit'] font-medium text-[#0F172A]">${intel.estimated_deal_size.low?.toLocaleString()} – ${intel.estimated_deal_size.high?.toLocaleString()}</p>
        </div>
      )}
      <p className="text-xs font-['JetBrains_Mono'] uppercase text-slate-400 mb-1">Urgency</p>
      {(() => {
        const signals = intel.urgency_signals || [];
        const level = intel.urgency_level || (signals.length >= 2 ? "high" : signals.length === 1 ? "medium" : "low");
        const levelColors = { high: "bg-red-50 text-red-700 border-red-200", medium: "bg-amber-50 text-amber-700 border-amber-200", low: "bg-green-50 text-green-700 border-green-200" };
        const levelLabel = { high: "High", medium: "Medium", low: "Low" };
        return (
          <div>
            <span className={`text-xs font-['JetBrains_Mono'] px-2 py-0.5 border ${levelColors[level] || levelColors.medium}`}>{levelLabel[level] || "Medium"}</span>
            {signals.length > 0 && (
              <div className="space-y-1 mt-2">
                {signals.map((u, idx) => (
                  <p key={`urg-${idx}`} className="text-xs font-['IBM_Plex_Sans'] text-amber-700">{u}</p>
                ))}
              </div>
            )}
          </div>
        );
      })()}
    </div>
  );
}
